"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams, useLocation } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import { useAuth } from "../context/AuthContext";
import { useFuncion } from "../context/FuncionContext";
import useFacultyScope from "../hooks/useFacultyScope";

// ==========================================
// HELPERS permisos
// ==========================================
function normalizePermCode(s) {
  return String(s || "").trim().toLowerCase().replace(/\s+/g, "_");
}

function hasPermission(permsSet, required) {
  if (!required) return true;
  const req = normalizePermCode(required);
  if (permsSet.has(req)) return true;

  const aliases = [
    req,
    req.replace(/^editar_/, "gestionar_"),
    req.replace(/^gestionar_/, "editar_"),
    req.replace(/_curso$/, ""),
    req.replace(/_curso$/, "_course"),
  ];
  return aliases.some((a) => permsSet.has(normalizePermCode(a)));
}

// ==========================================
// HELPERS roles (similar Header)
// ==========================================
function getRawRolesFromUser(user) {
  if (!user?.roles) return [];
  return user.roles
    .map((r) => {
      if (typeof r === "string") return r;
      return r.nombre || r.rol || r.role || "";
    })
    .filter(Boolean)
    .map((r) => String(r).toLowerCase());
}

function hasCourseRole(user, roleName) {
  const target = String(roleName || "").toLowerCase();

  const courseRolesObj = user?.course_roles || user?.curso_roles || null;
  if (courseRolesObj && typeof courseRolesObj === "object" && !Array.isArray(courseRolesObj)) {
    if (courseRolesObj[target]) return true;
  }

  const teacherRolesArr = user?.teacher_roles || user?.roles_docente || null;
  if (Array.isArray(teacherRolesArr)) {
    if (teacherRolesArr.some((r) => String(r).toLowerCase() === target)) return true;
  }

  return false;
}

function getAvailableRolesFromUser(user) {
  const baseRoles = getRawRolesFromUser(user);

  // globales: admin/estudiante/... (ignorar docente/asistente como globales)
  const globalRoles = [];
  baseRoles.forEach((r) => {
    if (r === "docente" || r === "asistente") return;
    globalRoles.push(r);
  });

  const rolesSet = new Set(globalRoles);

  if (hasCourseRole(user, "docente")) rolesSet.add("docente");
  if (hasCourseRole(user, "asistente")) rolesSet.add("asistente");

  // admin_facultad si tiene asignaciones
  if (Array.isArray(user?.admin_facultades) && user.admin_facultades.length > 0) {
    rolesSet.add("admin_facultad");
  }

  return Array.from(rolesSet);
}

export default function CourseManageRoute({ children, requiredPermission = null }) {
  const { user, currentRole, isAuthenticated } = useAuth();
  const { cursoId } = useParams();
  const location = useLocation();

  const { fetchTeacherCourses, getMyCoursePermissions, fetchCourses } = useFuncion();

  // ✅ HOOK CENTRAL
  const { isGlobalAdmin, isFacultyAdminActive, myIds: myFacultyIds } = useFacultyScope();

  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [denyReason, setDenyReason] = useState("");

  // ref de funciones (evitar loops)
  const fnsRef = useRef({ fetchTeacherCourses, getMyCoursePermissions, fetchCourses });
  useEffect(() => {
    fnsRef.current = { fetchTeacherCourses, getMyCoursePermissions, fetchCourses };
  }, [fetchTeacherCourses, getMyCoursePermissions, fetchCourses]);

  const hasSession = Boolean(isAuthenticated && user);

  const availableRoles = useMemo(() => getAvailableRolesFromUser(user), [user]);

  const effectiveRole = useMemo(() => {
    const cr = String(currentRole || "").toLowerCase().trim();
    if (cr) return cr;
    if (availableRoles.length > 0) return String(availableRoles[0] || "").toLowerCase();
    return "";
  }, [currentRole, availableRoles]);

  const roleReady = hasSession ? Boolean(effectiveRole) : true;

  const cid = Number(cursoId);
  const hasValidCourseId = Number.isFinite(cid) && cid > 0;

  const wantsTeacherRole = effectiveRole.startsWith("docente") || effectiveRole.startsWith("asistente");

  const requiredLabel = useMemo(() => {
    const map = {
      editar_estructura_curso: "Gestionar estructura del curso",
      inscribir_estudiantes_curso: "Inscribir estudiantes al curso",
      gestionar_examenes: "Gestionar exámenes del curso",
      editar_curso: "Editar datos del curso",
    };
    return map[normalizePermCode(requiredPermission)] || requiredPermission;
  }, [requiredPermission]);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    const finish = (opts) => {
      if (!alive) return;
      setAllowed(Boolean(opts?.allowed));
      setDenyReason(opts?.denyReason || "");
      setChecking(false);
    };

    setChecking(true);
    setAllowed(false);
    setDenyReason("");

    if (!hasSession) {
      finish({ allowed: false, denyReason: "" });
      return () => {
        alive = false;
        ctrl.abort();
      };
    }

    if (!hasValidCourseId) {
      finish({ allowed: false, denyReason: "curso_invalido" });
      return () => {
        alive = false;
        ctrl.abort();
      };
    }

    if (!roleReady) {
      // spinner hasta que llegue rol
      return () => {
        alive = false;
        ctrl.abort();
      };
    }

    // ==========================================================
    // ✅ ADMIN GLOBAL => acceso total
    // ==========================================================
    if (isGlobalAdmin) {
      finish({ allowed: true });
      return () => {
        alive = false;
        ctrl.abort();
      };
    }

    // ==========================================================
    // ✅ ADMIN FACULTAD (supremo scoped):
    // acceso total SOLO si el curso pertenece a sus facultades
    // ==========================================================
    if (isFacultyAdminActive) {
      (async () => {
        try {
          const res = await fnsRef.current.fetchCourses?.({
            page: 1,
            limit: 2000,
            showAll: true,
            _ts: Date.now(),
            signal: ctrl.signal,
          });

          if (!alive) return;

          const rows = Array.isArray(res) ? res : res?.courses || res?.items || res?.data || [];
          const found = rows.find((c) => String(c?.id) === String(cid));

          const courseFacultyId = Number(found?.facultad_id ?? found?.faculty_id ?? found?.id_facultad);
          const ok = Number.isFinite(courseFacultyId) && courseFacultyId > 0 && myFacultyIds.includes(courseFacultyId);

          if (!ok) {
            finish({ allowed: false, denyReason: "admin_facultad_no_aplica" });
            return;
          }

          // ✅ acceso total (ignorar requiredPermission)
          finish({ allowed: true });
        } catch (e) {
          console.error("Error validando admin_facultad:", e);
          finish({ allowed: false, denyReason: "error_validacion" });
        }
      })();

      return () => {
        alive = false;
        ctrl.abort();
      };
    }

    // ==========================================================
    // ✅ NO admin => debe ser docente/asistente
    // ==========================================================
    if (!wantsTeacherRole) {
      finish({ allowed: false, denyReason: "rol_no_docente" });
      return () => {
        alive = false;
        ctrl.abort();
      };
    }

    // ==========================================================
    // ✅ docente/asistente => validar asignación
    // ==========================================================
    (async () => {
      try {
        const res = await fnsRef.current.fetchTeacherCourses?.({
          page: 1,
          limit: 1000,
          _ts: Date.now(),
          signal: ctrl.signal,
        });

        if (!alive) return;

        const rows = Array.isArray(res) ? res : res?.courses || res?.items || res?.data || [];

        const getCourseId = (row) => {
          const c = row?.curso ?? row?.course ?? row?.curso_info ?? row?.course_info ?? row;
          return Number(c?.id ?? c?.curso_id ?? row?.curso_id ?? row?.course_id ?? row?.id);
        };

        const isAssignmentActive = (row) => {
          const raw =
            row?.asignacion_activa ??
            row?.asignacionActiva ??
            row?.is_active ??
            row?.isActive ??
            row?.isActiveAssignment ??
            row?.active ??
            row?.habilitado ??
            row?.enabled ??
            row?.vigente ??
            row?.estado ??
            row?.status ??
            row?.estado_asignacion ??
            row?.assignment_status ??
            null;

          if (raw === null || raw === undefined) return true;
          if (raw === true || raw === 1) return true;
          if (raw === false || raw === 0) return false;

          const s = String(raw).toLowerCase().trim();
          if (["activo", "activa", "active", "enabled", "habilitado", "vigente", "true", "t", "1"].includes(s)) return true;
          if (["inactivo", "inactiva", "inactive", "disabled", "deshabilitado", "false", "f", "0"].includes(s)) return false;
          return true;
        };

        const normalizeRole = (v) => String(v || "").toLowerCase().trim();
        const roleMatches = (row, effectiveRole) => {
          const er = normalizeRole(effectiveRole);

          const rawRole =
            row?.rol ??
            row?.role ??
            row?.nombre_rol ??
            row?.role_name ??
            row?.tipo ??
            row?.tipo_rol ??
            row?.asignacion_rol ??
            null;

          if (!rawRole) return true;

          const rr = normalizeRole(rawRole);
          if (er.startsWith("docente")) return rr.includes("docente") || rr.includes("teacher");
          if (er.startsWith("asistente")) return rr.includes("asistente") || rr.includes("assistant");
          return true;
        };

        const assignment = rows.find((row) => {
          const id = getCourseId(row);
          if (id !== cid) return false;
          if (!isAssignmentActive(row)) return false;
          if (!roleMatches(row, effectiveRole)) return false;
          return true;
        });

        if (!assignment) {
          finish({ allowed: false, denyReason: "no_asignado_para_rol" });
          return;
        }

        if (requiredPermission) {
          try {
            const myPermsData = await fnsRef.current.getMyCoursePermissions?.(cid, {
              signal: ctrl.signal,
            });

            if (!alive) return;

            const permsSet = new Set();

            if (Array.isArray(myPermsData?.perms)) {
              myPermsData.perms.forEach((p) => permsSet.add(normalizePermCode(p)));
            } else if (myPermsData?.perms && typeof myPermsData.perms === "object") {
              Object.entries(myPermsData.perms).forEach(([k, v]) => {
                if (v === true) permsSet.add(normalizePermCode(k));
              });
            } else if (Array.isArray(myPermsData)) {
              myPermsData.forEach((p) => permsSet.add(normalizePermCode(p)));
            }

            if (hasPermission(permsSet, requiredPermission)) {
              finish({ allowed: true });
            } else {
              finish({ allowed: false, denyReason: "sin_permiso" });
            }
          } catch (errPerm) {
            console.error("Error verificando permisos:", errPerm);
            finish({ allowed: false, denyReason: "error_validacion" });
          }
          return;
        }

        finish({ allowed: true });
      } catch (err) {
        console.error("Error en CourseManageRoute:", err);
        finish({ allowed: false, denyReason: "error_validacion" });
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [
    hasSession,
    hasValidCourseId,
    cid,
    roleReady,
    effectiveRole,
    wantsTeacherRole,
    requiredPermission,
    requiredLabel,
    isGlobalAdmin,
    isFacultyAdminActive,
    myFacultyIds.join("|"),
  ]);

  // ==========================================
  // RENDER
  // ==========================================
  if (!hasSession) return <Navigate to="/login" replace state={{ from: location }} />;

  if (!hasValidCourseId) return <Navigate to="/" replace />;

  if (!roleReady || checking) {
    return (
      <div style={{ padding: 40 }}>
        <LoadingSpinner message="Verificando permisos de acceso..." />
      </div>
    );
  }

  if (!allowed) {
    const rolePretty = effectiveRole.includes("docente")
      ? "Docente"
      : effectiveRole.includes("asistente")
      ? "Asistente"
      : effectiveRole || "Usuario";

    let msg = "No tienes acceso a esta sección.";

    if (denyReason === "rol_no_docente") {
      msg = `Esta ruta requiere perfil docente/asistente (o admin). Tu rol actual es: ${rolePretty}.`;
    } else if (denyReason === "no_asignado_para_rol") {
      msg = `No estás asignado (o tu asignación no está activa) en el curso #${cid} con el rol de ${rolePretty}.`;
    } else if (denyReason === "sin_permiso") {
      msg = `Tu rol de ${rolePretty} no tiene el permiso: "${requiredLabel}". Contacta al administrador.`;
    } else if (denyReason === "error_validacion") {
      msg = "Error validando permisos. Revisa tu conexión o intenta recargar.";
    } else if (denyReason === "admin_facultad_no_aplica") {
      msg = `Eres admin de facultad, pero este curso (#${cid}) no pertenece a una de tus facultades asignadas.`;
    }

    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          maxWidth: 600,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            background: "white",
            padding: 32,
            borderRadius: 16,
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            border: "1px solid #fee2e2",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>⛔</div>
          <h2 style={{ color: "#b91c1c", marginBottom: 12, marginTop: 0 }}>Acceso denegado</h2>
          <p style={{ color: "#4b5563", marginBottom: 24, lineHeight: 1.6, fontSize: "1.05rem" }}>{msg}</p>

          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button
              onClick={() => (window.location.href = "/admin/cursos")}
              style={{
                background: "white",
                color: "#374151",
                border: "1px solid #d1d5db",
                padding: "10px 24px",
                borderRadius: 10,
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Volver a mis cursos
            </button>

            <button
              onClick={() => window.location.reload()}
              style={{
                background: "#2563eb",
                color: "white",
                border: "none",
                padding: "10px 24px",
                borderRadius: 10,
                cursor: "pointer",
                fontWeight: 700,
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
              }}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}